/**
 * Resolves the trusted `id` the app sends to the signaling server's /register
 * endpoint. The server trusts this id (it authenticates the user via their
 * cloud provider); it only makes abuse costly with rate limits keyed on a
 * server-side hash of the id (see the y-webrtc repo's bin/auth.js).
 *
 * Order: the active cloud provider's verified identity (Nextcloud/OneDrive/
 * Dropbox/Google Drive), else a stable per-device random id. The device id is a
 * UUIDv4 (122 random bits), so it can't realistically collide with another
 * user's id — and since the server buckets rate limits per hashed id, distinct
 * ids get independent budgets, so a device id can never rate-limit unrelated
 * users. This keeps collaboration working for local-only / S3 / signed-out
 * users, just rate-limited per device.
 */
import sync from "../cloud/sync";

const DEVICE_ID_KEY = "yaqadocs.collab.deviceId";

function randomId(): string {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch {
    /* fall through */
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

/** A stable per-device id, persisted in localStorage. Namespaced `device:<uuid>`. */
export function getOrCreateDeviceId(): string {
  try {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id = randomId();
      localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return `device:${id}`;
  } catch {
    // localStorage unavailable (private mode / quota) — an ephemeral per-session
    // id is still unique enough to avoid colliding with other users' buckets.
    return `device:${randomId()}`;
  }
}

/**
 * The verified cloud identity if the active provider can supply one
 * non-interactively, else a stable device id. Always resolves to a non-empty id
 * so collaboration can always mint a signaling key.
 */
export async function resolveCollabIdentity(): Promise<string> {
  try {
    const id = await sync.getIdentity();
    if (id) return id;
  } catch {
    /* ignore — fall back to the device id */
  }
  return getOrCreateDeviceId();
}
